import {
    GetContextResponse,
    GetFundingRequirementsCreditFacilityResponse,
    GetStateResponse
} from '@iwoca/lapi-client/edge';

import {
    ADD_PHONE_NUMBER,
    CHANGE_PHONE_NUMBER,
    ID_CHECK_VERIFICATION_DATA,
    ID_CHECK_MAXIMUM_ATTEMPTS,
    PHONE_NUMBER_VALIDATION_ERROR,
    SEND_VERIFICATION_SMS_FAIL,
    SEND_VERIFICATION_SMS_PENDING,
    SEND_VERIFICATION_SMS_SUCCESS,
    TOGGLE_ADD_PHONE_NUMBER
} from './IdCheckVerificationModal.actions';
import { TDeserialisedPeople } from './IdCheckVerificationModal.types';
import {
    deserialiseIdCheckResponse,
    toggleAddPhoneNumber,
    updatePeopleState
} from './IdCheckVerificationModalHelper';
import { mergeStates } from '../../../iwProvider/StoreHelper';

const initialState: TIdCheckState = {
    peopleToVerify: [],
    reachedMaxIdCheckAttempts: false,
    id_verification_provider_name: 'Onfido'
};

type TAction =
    | {
          type: typeof SEND_VERIFICATION_SMS_PENDING;
          personUID: string;
      }
    | {
          type: typeof SEND_VERIFICATION_SMS_FAIL;
          personUID: string;
      }
    | {
          type: typeof SEND_VERIFICATION_SMS_SUCCESS;
          personUID: string;
      }
    | {
          type: typeof PHONE_NUMBER_VALIDATION_ERROR;
          personUID: string;
      }
    | {
          type: typeof CHANGE_PHONE_NUMBER;
          personUID: string;
          phoneNumber: string;
      }
    | {
          type: typeof ADD_PHONE_NUMBER;
          personUID: string;
          phoneNumber: string;
      }
    | {
          type: typeof TOGGLE_ADD_PHONE_NUMBER;
          personUID: string;
      }
    | {
          type: typeof ID_CHECK_VERIFICATION_DATA;
          customer_state: GetStateResponse['data'];
          funding_requirement: GetFundingRequirementsCreditFacilityResponse['data'];
          site_context: GetContextResponse['data'];
      }
    | {
          type: typeof ID_CHECK_MAXIMUM_ATTEMPTS;
      };

type TIdCheckState = {
    peopleToVerify: TDeserialisedPeople;
    reachedMaxIdCheckAttempts: boolean;
    id_verification_provider_name: 'Onfido' | 'IDVerse';
    SMSPending?: boolean;
    status?:
        | 'Verified'
        | 'Verifying your check'
        | 'Something went wrong'
        | 'Not started yet'
        | 'Something went wrong';
};

// eslint-disable-next-line import/no-default-export
export default function (
    state: TIdCheckState = initialState,
    action: TAction
): TIdCheckState {
    switch (action.type) {
        case SEND_VERIFICATION_SMS_PENDING:
            return mergeStates(state, {
                peopleToVerify: updatePeopleState(
                    state.peopleToVerify,
                    action.personUID,
                    { SMSPending: true }
                )
            });
        case SEND_VERIFICATION_SMS_FAIL:
            return mergeStates(state, {
                peopleToVerify: updatePeopleState(
                    state.peopleToVerify,
                    action.personUID,
                    { SMSPending: false, status: 'SMS failed' }
                )
            });

        case SEND_VERIFICATION_SMS_SUCCESS:
            return mergeStates(state, {
                peopleToVerify: updatePeopleState(
                    state.peopleToVerify,
                    action.personUID,
                    { SMSPending: false, status: 'SMS sent' }
                )
            });
        case PHONE_NUMBER_VALIDATION_ERROR:
            return mergeStates(state, {
                peopleToVerify: updatePeopleState(
                    state.peopleToVerify,
                    action.personUID,
                    { error: true }
                )
            });
        case CHANGE_PHONE_NUMBER:
            return mergeStates(state, {
                peopleToVerify: updatePeopleState(
                    state.peopleToVerify,
                    action.personUID,
                    { newPhoneNumber: action.phoneNumber, error: false }
                )
            });
        case ADD_PHONE_NUMBER:
            return mergeStates(state, {
                peopleToVerify: updatePeopleState(
                    state.peopleToVerify,
                    action.personUID,
                    { addPhoneNumberPending: true }
                )
            });
        case TOGGLE_ADD_PHONE_NUMBER:
            return mergeStates(state, {
                peopleToVerify: toggleAddPhoneNumber(
                    state.peopleToVerify,
                    action.personUID
                )
            });
        case ID_CHECK_VERIFICATION_DATA:
            return {
                ...state,
                ...deserialiseIdCheckResponse(
                    action.customer_state,
                    action.funding_requirement,
                    action.site_context
                )
            };

        case ID_CHECK_MAXIMUM_ATTEMPTS:
            return mergeStates(state, { reachedMaxIdCheckAttempts: true });
        default:
            return state;
    }
}
